import {
  CustomRole,
  CustomRolePermission,
  CustomRoleResult,
  PermissionCategory,
  PermissionLevel,
  ScopeMode,
} from '@/client/custom-roles';
import { Role } from '@/client/roles';
import { TranslationFunctionType } from '@/common/types';
import { RoleCardPill } from '@/components/custom-roles';

export const PERMISSION_CATEGORY_ORDER: PermissionCategory[] = [
  PermissionCategory.REPORTING,
  PermissionCategory.USERS_AND_GROUPS,
  PermissionCategory.ACCOUNT_SETTINGS,
  PermissionCategory.RESOURCES,
  PermissionCategory.CONTENT,
  PermissionCategory.TRAINING,
];

export const hasRestrictedUserScope = (scopeMode?: ScopeMode | null): boolean =>
  scopeMode === ScopeMode.GROUPS || scopeMode === ScopeMode.MANAGER;

export const isManagerScoped = (scopeMode?: ScopeMode | null): boolean =>
  scopeMode === ScopeMode.MANAGER;

export const levelOf = (
  customRole: CustomRole,
  category: PermissionCategory,
): PermissionLevel =>
  customRole.permissions.find((permission) => permission.category === category)
    ?.level ?? PermissionLevel.NONE;

export const toPermissionSummaries = (
  customRole: CustomRole,
  t: TranslationFunctionType,
): RoleCardPill[] =>
  PERMISSION_CATEGORY_ORDER.map((category) => {
    const level = levelOf(customRole, category);

    return {
      key: category,
      label: `${t(`account.rolesAccess.category.${category}.title`)}: ${t(
        `account.rolesAccess.level.${level}`,
      )}`,
      level,
    };
  });

export const scopeModeLabel = (
  scopeMode: ScopeMode | null | undefined,
  t: TranslationFunctionType,
): string =>
  scopeMode
    ? t('account.rolesAccess.scopeMode.summary', {
        mode: t(`account.rolesAccess.scopeMode.${scopeMode}`),
      })
    : '';

export const customRoleToPermissionLevels = (
  customRole: CustomRole,
): Record<PermissionCategory, PermissionLevel> =>
  PERMISSION_CATEGORY_ORDER.reduce(
    (levels, category) => ({
      ...levels,
      [category]: levelOf(customRole, category),
    }),
    {} as Record<PermissionCategory, PermissionLevel>,
  );

export const permissionLevelsToPermissions = (
  levels: Record<PermissionCategory, PermissionLevel>,
): CustomRolePermission[] =>
  PERMISSION_CATEGORY_ORDER.map((category) => ({
    category,
    level: levels[category],
  }));

const CUSTOM_ROLE_OPTION_PREFIX = 'custom-role:';

export type CustomRoleOption = Role & {
  customRoleId: string;
  scopeMode: ScopeMode | null;
};

export const toCustomRoleOptions = (
  customRoles: CustomRoleResult[],
  learnerRole?: Role,
): CustomRoleOption[] =>
  learnerRole
    ? customRoles.map(({ customRole }) => ({
        ...learnerRole,
        id: `${CUSTOM_ROLE_OPTION_PREFIX}${customRole.id}`,
        name: customRole.name,
        customRoleId: customRole.id,
        scopeMode: customRole.scopeMode ?? null,
      }))
    : [];

export const asCustomRoleOption = (
  role?: Role | null,
): CustomRoleOption | undefined =>
  role?.id?.startsWith(CUSTOM_ROLE_OPTION_PREFIX)
    ? (role as CustomRoleOption)
    : undefined;

export const resolveRoleAssignment = (
  role: Role,
  learnerRoleId?: string,
): { role: string; customRole: string | null } => {
  const customRoleOption = asCustomRoleOption(role);

  return customRoleOption
    ? {
        role: learnerRoleId as string,
        customRole: customRoleOption.customRoleId,
      }
    : { role: role?.id, customRole: null };
};
